/**
 * Which sidebar nav item is "active" (highlighted) for the current path.
 *
 * The rule that matters: an item matches when the current path EQUALS its href
 * path or is a true SUB-PATH (`href + '/…'`) — never a bare `startsWith`, which
 * lit up every item whose href is merely a prefix (e.g. a child's Home
 * `/parent/<id>` matched `/parent/<id>/calendar`, so Home and Schedule both
 * showed as active at once).
 *
 * The most-specific match wins and, on ties (two items sharing an href), the
 * FIRST item — so exactly one item is ever active.
 *
 * An item marked `exact` matches only its own path, never a sub-path. A
 * child's Home (`/parent/<id>`) is exact: its sub-pages (`/details`, `/forms`,
 * `/medications`…) are not "Home", so Home must not light on them.
 *
 * Query strings on an href (e.g. `?report=1`) are ignored for matching.
 *
 * Returns the index of the active item, or -1 if none match.
 */
export type NavMatchItem = string | { href: string; exact?: boolean };

export function activeNavIndex(pathname: string, items: readonly NavMatchItem[]): number {
  const hrefPath = (href: string) => href.split('?')[0];
  const matches = (item: NavMatchItem) => {
    const p = hrefPath(typeof item === 'string' ? item : item.href);
    const exact = typeof item !== 'string' && item.exact === true;
    return pathname === p || (!exact && pathname.startsWith(p + '/'));
  };

  let bestIdx = -1;
  let bestLen = -1;
  items.forEach((item, i) => {
    const len = hrefPath(typeof item === 'string' ? item : item.href).length;
    if (matches(item) && len > bestLen) {
      bestIdx = i;
      bestLen = len;
    }
  });
  return bestIdx;
}
